import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  Popconfirm,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';
import useColumnSearch from 'modules/layoutModule/hooks/useColumnSearch';
import {
  getDistributionRequest,
  removeSpecificDistributionRequest,
} from '../requests/contactRequests';
import { getProvincesRequest } from '../../sectionsModule/requests/sectionsRequests';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
  isActive: number;
  deleteDisabled?: boolean;
}

const Distribution = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState('');
  const [provinces, setProvinces] = useState([]);

  const confirmDelete = (id: number) => {
    removeSpecificDistributionRequest(id).then(() => {
      downloadDistribution();
    });
  };

  const downloadDistribution = () =>
    getDistributionRequest().then((response) => {
      if (response) {
        const { data } = response;
        setTableData(data);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (tableData.length === 0) {
      downloadDistribution();

      getProvincesRequest().then((response) => {
        if (response) {
          const sections = response.map(
            (section: { name: string; id: number }) => {
              return {
                label: section.name,
                value: section.id,
              };
            }
          );
          setProvinces(sections);
        }
      });
    }
  }, [tableData]);

  const columns = [
    {
      title: 'Nazwa',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      ...useColumnSearch(
        'name',
        'Szukaj po nazwie',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
    },
    {
      title: 'Województwo',
      dataIndex: 'provinceName',
      key: 'provinceName',
      filters:
        provinces && provinces.length > 0
          ? provinces?.map((province: any) => ({
              text: province.label,
              value: province.value,
            }))
          : [],
      onFilter: (value: string, record: any) => record.id_province === value,
    },
    {
      title: 'Kod pocztowy',
      dataIndex: 'zipcode',
      key: 'zipcode',
    },
    {
      title: 'Miasto',
      dataIndex: 'city',
      key: 'city',
      sorter: (a: { city: string }, b: { city: string }) =>
        a.city.localeCompare(b.city),
      ...useColumnSearch(
        'city',
        'Szukaj po mieście',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
    },
    {
      title: 'Adres',
      dataIndex: 'address',
      key: 'address',
      sorter: (a: { address: string }, b: { address: string }) =>
        a.address.localeCompare(b.address),
      ...useColumnSearch(
        'address',
        'Szukaj po adresie',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
    },
    {
      title: 'Numer telefonu',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Aktywna',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '150px',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />
      ),
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (text: string, item: NewsTableTypes) => {
        return (
          <Space
            size="middle"
            style={{ display: 'Flex', flexDirection: 'column' }}
          >
            <Tooltip title="Edytuj wpis">
              <Link to={`/distribution/${item.id}`}>
                <Button type="primary" shape="circle" icon={<EditOutlined />} />
              </Link>
            </Tooltip>
            <Tooltip title="Usuń wpis">
              <Popconfirm
                title="Czy na pewno chcesz usunąc ten wpis?"
                placement="left"
                onConfirm={() => confirmDelete(item.id)}
                okText="Tak"
                cancelText="Nie"
              >
                <Button type="link" disabled={item.isActive === 1}>
                  <DeleteFilled />
                </Button>
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Kontakt</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/distribution">Punkty dystrybucji</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default Distribution;
